import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Calculator, X, Users, Baby, Flame, Sparkles } from "lucide-react";

export default function ParrilladaCalculator({ open, onClose }) {
  const [adults, setAdults] = useState(4);
  const [kids, setKids] = useState(0);
  const [appetite, setAppetite] = useState("normal");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!open) return null;

  const calculate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculator/recommend", { adults, kids, appetite });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  const goToPkg = () => {
    if (result?.recommended_package) {
      navigate(`/paquete/${result.recommended_package.slug}`);
      onClose();
    }
  };

  const close = () => {
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="calculator-modal">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg bg-arre-surface border border-arre-border grain max-h-[90vh] overflow-y-auto">
        <button onClick={close} className="absolute top-4 right-4 text-white/60 hover:text-white z-10" data-testid="calc-close">
          <X className="w-5 h-5" />
        </button>
        <div className="p-8 md:p-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-arre-red/15 border border-arre-red/40 text-arre-red text-[10px] tracking-[0.3em] uppercase mb-4">
            <Calculator className="w-3 h-3" />
            Calculadora Compare
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-wider text-white uppercase leading-[0.9]">
            ¿Cuántos<br /><span className="text-arre-red">compares?</span>
          </h2>
          <p className="text-white/60 text-sm mt-3">
            Te decimos cuánta carne necesitas y qué paquete te queda perfecto.
          </p>

          {!result ? (
            <div className="mt-6 space-y-5">
              <NumberInput icon={Users} label="Adultos" value={adults} onChange={setAdults} min={1} testid="calc-adults" />
              <NumberInput icon={Baby} label="Niños (menores de 12)" value={kids} onChange={setKids} min={0} testid="calc-kids" />

              <div>
                <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-white/60 mb-2">
                  <Flame className="w-3.5 h-3.5" />
                  Nivel de hambre
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: "light", l: "Ligero", sub: "300g" },
                    { v: "normal", l: "Normal", sub: "400g" },
                    { v: "heavy", l: "Brutal", sub: "550g" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => setAppetite(o.v)}
                      className={`border p-3 transition ${
                        appetite === o.v ? "border-arre-red bg-arre-red/10" : "border-arre-border bg-arre-black hover:border-white/40"
                      }`}
                      data-testid={`calc-appetite-${o.v}`}
                    >
                      <div className={`font-display text-lg tracking-wider uppercase ${appetite === o.v ? "text-arre-red" : "text-white"}`}>{o.l}</div>
                      <div className="text-[10px] text-white/50">{o.sub}/persona</div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={calculate}
                disabled={loading}
                className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase py-4 disabled:opacity-60"
                data-testid="calc-submit"
              >
                {loading ? "Calculando..." : "Calcular mi parrillada →"}
              </button>
            </div>
          ) : (
            <div className="mt-6 space-y-5" data-testid="calc-result">
              <div className="bg-arre-black border border-arre-red/40 p-5">
                <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-arre-red">
                  <Sparkles className="w-3.5 h-3.5" />
                  Recomendación
                </div>
                <div className="mt-3 text-3xl font-display tracking-wider text-white">
                  {result.total_kg} kg de carne
                </div>
                <div className="text-white/70 text-sm mt-2">Para {result.people} persona{result.people > 1 ? "s" : ""}</div>
              </div>

              {result.recommended_package ? (
                <div className="bg-arre-black border border-arre-border p-5">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-arre-red mb-1">Paquete ideal</div>
                  <div className="flex gap-4 items-start">
                    <img src={result.recommended_package.image} alt={result.recommended_package.name} className="w-20 h-24 object-cover border border-arre-border" />
                    <div className="flex-1">
                      <div className="font-display text-2xl tracking-wider text-white leading-tight">{result.recommended_package.name}</div>
                      <div className="text-xs italic text-white/60 mt-1">{result.recommended_package.tagline}</div>
                      <div className="font-display text-2xl text-white mt-2">{fmtMXN(result.recommended_package.price)}</div>
                    </div>
                  </div>
                  <button
                    onClick={goToPkg}
                    className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase py-3 mt-4"
                    data-testid="calc-goto-pkg"
                  >
                    Ver paquete →
                  </button>
                </div>
              ) : (
                <div className="bg-arre-black border border-arre-border p-5">
                  <p className="text-white/80 text-sm">{result.message}</p>
                  <button
                    onClick={() => { navigate("/arma-tu-paquete"); onClose(); }}
                    className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase py-3 mt-4"
                  >
                    Arma tu paquete →
                  </button>
                </div>
              )}

              <button
                onClick={() => setResult(null)}
                className="w-full text-center text-xs tracking-[0.25em] uppercase text-white/60 hover:text-white"
                data-testid="calc-reset"
              >
                Calcular de nuevo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NumberInput({ icon: Icon, label, value, onChange, min = 0, testid }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-white/60 mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="flex items-center border border-arre-border bg-arre-black">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          className="px-4 py-3 text-white hover:bg-arre-surface"
        >−</button>
        <input
          type="number"
          min={min}
          value={value}
          onChange={(e) => onChange(Math.max(min, parseInt(e.target.value) || min))}
          className="flex-1 bg-transparent text-white text-center font-display text-2xl tracking-wider py-3 focus:outline-none"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="px-4 py-3 text-white hover:bg-arre-surface"
        >+</button>
      </div>
    </div>
  );
}
